/**
 * Drawer — right-side sliding panel. Used for position detail and
 * signal-decision viewers in Phase 5.
 *
 * Why a custom component instead of headlessui / radix:
 *   - Bundle size: we already keep the dep list short.
 *   - Visual: needs the terminal look (sharp borders, ink/elevated
 *     surfaces) — easier to own.
 *
 * UX:
 *   - Click on backdrop or Esc → onClose
 *   - Scroll inside the panel; backdrop fades the rest of the app.
 *   - No animation on first render, but slide-in on subsequent opens.
 */
import { useEffect, type ReactNode } from 'react'
import { cn } from '@/lib/cn'

interface DrawerProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  /** Tailwind width class (default `w-[480px]`). */
  widthClassName?: string
}

export function Drawer({
  open, onClose, title, subtitle, children,
  widthClassName = 'w-[480px]',
}: DrawerProps) {
  // Esc closes; lock body scroll while open.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Panel */}
      <aside
        className={cn(
          'absolute top-0 right-0 h-full',
          'bg-surface border-l border-line shadow-2xl',
          'flex flex-col',
          'max-w-[100vw]',
          widthClassName,
        )}
      >
        <header className="flex items-baseline gap-3 px-5 py-4 border-b border-line">
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold truncate">{title}</h2>
            {subtitle && (
              <p className="text-xs text-textFaint mt-0.5 truncate">
                {subtitle}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-textDim hover:text-text text-xl leading-none"
          >
            ×
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  )
}
